import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Checkbox, Tooltip, Row, Col } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';

import TranslationDTO from 'src/models/generic/TranslationDTO';
import KeyValueListDTO from 'src/models/generic/KeyValueListDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import AddAcademicStandardDTO from 'src/models/academic/AddAcademicStandardDTO';
import UpdateAcademicStandardDTO from 'src/models/academic/UpdateAcademicStandardDTO';
import AcademicApiController from 'src/api/AcademicApiController';
import StatementApiController from 'src/api/StatementApiController';
import GameApiController from 'src/api/GameApiController';
import ActivityApiController from 'src/api/ActivityApiController';
import StatementDropdownDTO from 'src/models/statement/StatementDropdownDTO';
import ArrayUtil from 'src/utils/ArrayUtil';


interface RouteParams {
    id: string;
}

interface AddEditAcademicStandardProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditAcademicStandardState {
    loading: boolean;
    isSubmitting: boolean;
    currentAcademicStandard: UpdateAcademicStandardDTO;
    allActivities: KeyValueDTO<string, number>[];
    allGames: KeyValueDTO<string, number>[];
    allStatements: StatementDropdownDTO[];
    allFrameworks: KeyValueDTO<string, number>[];
    redirect: boolean;
}

interface AcademicStandardFormProps {
    currentAcademicStandard: UpdateAcademicStandardDTO;
    allActivities: KeyValueDTO<string, number>[];
    allGames: KeyValueDTO<string, number>[];
    allStatements: StatementDropdownDTO[];
    allFrameworks: KeyValueDTO<string, number>[];
    translate: (id: any) => any;
    onChange: (changedFields: any[]) => void;
}

// Form
let AcademicStandardForm = Form.create({
    onFieldsChange(props: AcademicStandardFormProps, changedFields: any[]) {
        props.onChange(changedFields);
    }
})(class extends React.Component<AcademicStandardFormProps & FormComponentProps> {
    constructor(props: AcademicStandardFormProps & FormComponentProps) {
        super(props);
    }

    private generateOptionsForSelect(keyValues: KeyValueDTO<string, number>[]): JSX.Element[] {
        return keyValues.map(keyValue => <Select.Option value={keyValue.value} key={keyValue.key} title={keyValue.key}>{keyValue.key}</Select.Option>);
    }

    private renderStandardFormItem(translationId: string, id: string, initialValue, inputType, visible: boolean = true, required: boolean = true) {
        let translatedLabel = this.props.translate(translationId);

        if (visible) {
            return (
                <Form.Item label={translatedLabel}>
                    {this.props.form.getFieldDecorator(id, {
                        initialValue: initialValue,
                        rules: [{ required: required, message: this.props.translate('admin.common.requiredRule') }]
                    })(inputType)}
                </Form.Item>
            );
        }
    }

    private generateStatementOptions(statements: StatementDropdownDTO[]) {
        return ArrayUtil.Unique(statements.map(x => x.statementType)) // Get the types (State/District/School/Generic)
            .map(statementType => ({ key: statementType, values: statements.filter(x => x.statementType === statementType) })) // Split based on Type
            .map(x => (<Select.OptGroup label={x.key}>
                {x.values.map(y => <Select.Option value={y.statementId} key={y.statementText} title={y.statementText}>{y.statementText}</Select.Option>)}
            </Select.OptGroup>));
    }

    render() {
        return (
            <Row>
                <Col span={12}>
                    <Form className="admin-form">
                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem1",
                            "standard",
                            this.props.currentAcademicStandard.standard,
                            <Input autoComplete="off" />
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem2",
                            "description",
                            this.props.currentAcademicStandard.description,
                            <Input autoComplete="off" />
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem3",
                            "academicStandardFrameworkId",
                            this.props.currentAcademicStandard.academicStandardFrameworkId,
                            <Select>{this.generateOptionsForSelect(this.props.allFrameworks)}</Select>
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem4",
                            "activityIds",
                            this.props.currentAcademicStandard.activityIds,
                            <Select optionFilterProp="title" mode="multiple">{this.generateOptionsForSelect(this.props.allActivities)}</Select>,
                            true,
                            false
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem5",
                            "gameIds",
                            this.props.currentAcademicStandard.gameIds,
                            <Select optionFilterProp="title" mode="multiple">{this.generateOptionsForSelect(this.props.allGames)}</Select>,
                            true,
                            false
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditAcademicStandard.formItem6",
                            "statementIds",
                            this.props.currentAcademicStandard.statementIds,
                            <Select optionFilterProp="title" mode="multiple">
                                {this.generateStatementOptions(this.props.allStatements)}
                            </Select>,
                            true,
                            false
                        )}
                    </Form>
                </Col>
            </Row>
        );
    }
});

class AddEditAcademicStandard extends React.Component<AddEditAcademicStandardProps, AddEditAcademicStandardState> {
    private formRef: React.Component<FormComponentProps>;

    constructor(props: AddEditAcademicStandardProps) {
        super(props);
        bindAllOfThis(this, AddEditAcademicStandard.prototype);

        let academicStandardId = parseInt(this.props.match.params.id, 10);

        let currentAcademicStandard: UpdateAcademicStandardDTO = {
            academicStandardId: academicStandardId,
            academicStandardFrameworkId: null,
            standard: "",
            description: "",
            activityIds: [],
            gameIds: [],
            statementIds: []
        };

        this.state = {
            loading: true,
            isSubmitting: false,
            redirect: false,
            allActivities: [],
            allGames: [],
            allStatements: [],
            allFrameworks: [],
            currentAcademicStandard: currentAcademicStandard
        };
    }

    componentWillMount() {
        let activitiesPromise = ActivityApiController.getActivitiesForAcademicStandard();
        let gamesPromise = GameApiController.getGamesForAcademicStandard();
        let statementsPromise = StatementApiController.getStatementsForAcademicStandard();
        let frameworksPromise = AcademicApiController.getAcademicStandardFrameworksForAcademicStandard();

        Promise.all([activitiesPromise, gamesPromise, statementsPromise, frameworksPromise]).then(results => {
            let activities = results[0].data.keyValues;
            let games = results[1].data.keyValues;
            let statements = results[2].data;
            let frameworks = results[3].data.keyValues;

            this.setState({
                allActivities: activities,
                allGames: games,
                allStatements: statements,
                allFrameworks: frameworks
            }, this.fillForm);
        });
    }

    private fillForm(): void {
        if(this.state.currentAcademicStandard.academicStandardId) {
            AcademicApiController.getAcademicStandard(this.state.currentAcademicStandard.academicStandardId).then((result) => {
                this.setState({
                    loading: false,
                    currentAcademicStandard: result.data
                });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditAcademicStandard.messageRetrieveError'));
            });
        } else {
            this.setState({
                loading: false
            });
        }
    }

    private handleInputChange(changedFields: any[]): void {
        let newState = {};
        for(var key in changedFields) {
            if (changedFields.hasOwnProperty(key)) {
                let field: any = changedFields[key];
                newState[key] = field.value;
            }
        }
        this.setState({
            currentAcademicStandard: { ...this.state.currentAcademicStandard, ...newState }
        });
    }

    private addAcademicStandard(): void {
        let addAcademicStandardDTO: AddAcademicStandardDTO = {
            academicStandardFrameworkId: this.state.currentAcademicStandard.academicStandardFrameworkId,
            standard: this.state.currentAcademicStandard.standard,
            description: this.state.currentAcademicStandard.description,
            activityIds: this.state.currentAcademicStandard.activityIds,
            gameIds: this.state.currentAcademicStandard.gameIds,
            statementIds: this.state.currentAcademicStandard.statementIds
        };

        AcademicApiController.addAcademicStandard(addAcademicStandardDTO)
            .then(result => {
                message.success(this.props.translate('admin.addEditAcademicStandard.messageAdd'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditAcademicStandard.messageAddError'));
                this.setState({ isSubmitting: false });
            });
    }

    private updateAcademicStandard(): void {
        AcademicApiController.updateAcademicStandard(this.state.currentAcademicStandard)
            .then(result => {
                message.success(this.props.translate('admin.addEditAcademicStandard.messageUpdate'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditAcademicStandard.messageUpdateError'));
                this.setState({ isSubmitting: false });
            });
    }

    private handleSubmit(e): void {
        e.preventDefault();

        this.formRef.props.form.validateFields((error: any, values: any) => {
            if (!error) {
                this.setState({ isSubmitting: true });
                this.state.currentAcademicStandard.academicStandardId ? this.updateAcademicStandard() : this.addAcademicStandard();
            }
        });
    }

    private handleCancel(e): void {
        this.setState({ redirect: true });
    }

    render() {
        if(this.state.redirect) {
            return <Redirect to={Routes.ADMIN_ACADEMICS} push />;
        } else if(this.state.loading) {
            return <Spin />;
        }

        let formProps = {
            allActivities: this.state.allActivities,
            allGames: this.state.allGames,
            allStatements: this.state.allStatements,
            allFrameworks: this.state.allFrameworks,
            currentAcademicStandard: this.state.currentAcademicStandard,
            translate: this.props.translate,
            onChange: this.handleInputChange
        } as AcademicStandardFormProps;

        return(
            <div>
                <h2>{this.props.translate('admin.addEditAcademicStandard.addHeader')}</h2>

                <AcademicStandardForm wrappedComponentRef={ref => this.formRef = ref} {...formProps} />

                <div>
                    <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
                        {this.props.translate("admin.common.saveButton")}
                    </Button>
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </div>
            </div>
        );
    }
}

export default withLocalize(AddEditAcademicStandard);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditAcademicStandard.tsx